import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiMinus, FiPlus, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useScrollReveal } from '../hooks/useGsapMotion';

const Cart = () => {
  const { items, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const pageRef = useRef(null);

  useScrollReveal(pageRef, [items.length]);

  const handleQuantityChange = (productId, nextQty) => {
    if (nextQty <= 0) {
      removeFromCart(productId);
      toast.success('Item removed from cart');
      return;
    }
    updateQuantity(productId, nextQty);
  };

  if (items.length === 0) {
    return (
      <div className="sc-container py-20 text-center" ref={pageRef}>
        <div className="mx-auto max-w-lg rounded-3xl border border-slate-200 bg-white p-10 shadow-soft" data-reveal>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Your basket</p>
          <h2 className="text-3xl font-black text-[#1A1A1A]">Your cart is empty</h2>
          <p className="mt-3 text-sm text-slate-500">
            Add premium picks from our catalog and they will appear here.
          </p>
          <Link to="/" className="sc-btn-primary mt-6 inline-block">
            Continue shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="sc-container py-8 md:py-10">
      <section className="mb-6 flex flex-wrap items-center justify-between gap-3" data-reveal>
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Checkout prep</p>
          <h1 className="text-3xl font-black text-[#1A1A1A]">Shopping Cart</h1>
        </div>
        <button
          onClick={() => {
            clearCart();
            toast.success('Cart cleared');
          }}
          className="sc-btn-ghost"
        >
          Clear cart
        </button>
      </section>

      <div className="grid grid-cols-1 gap-7 lg:grid-cols-12">
        <section className="space-y-4 lg:col-span-8" data-stagger>
          {items.map((item) => (
            <article
              key={item.id}
              className="sc-soft-card flex flex-col gap-4 p-4 sm:flex-row sm:items-center"
              data-stagger-item
            >
              <img
                src={item.image || '/placeholder-product.jpg'}
                alt={item.name}
                className="h-24 w-full rounded-xl object-cover sm:h-20 sm:w-20"
              />

              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold text-[#1A1A1A]">{item.name}</h3>
                <p className="mt-1 text-sm text-slate-500">Unit price: ৳{item.price}</p>
              </div>

              <div className="flex items-center justify-between gap-4 sm:justify-end">
                <div className="flex items-center rounded-full border border-slate-200 px-1">
                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                    className="rounded-full p-2 text-slate-500 transition hover:text-[#DB4444]"
                  >
                    <FiMinus size={13} />
                  </button>
                  <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                    className="rounded-full p-2 text-slate-500 transition hover:text-[#DB4444]"
                  >
                    <FiPlus size={13} />
                  </button>
                </div>

                <div className="text-right">
                  <p className="text-sm font-bold text-[#1A1A1A]">৳{(item.price * item.quantity).toFixed(2)}</p>
                </div>

                <button
                  onClick={() => {
                    removeFromCart(item.id);
                    toast.success('Item removed from cart');
                  }}
                  className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-[#DB4444] hover:text-[#DB4444]"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            </article>
          ))}
        </section>

        <aside className="lg:col-span-4" data-reveal>
          <div className="sc-soft-card p-6">
            <h2 className="text-xl font-black text-[#1A1A1A]">Order Summary</h2>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-semibold text-[#1A1A1A]">৳{getCartTotal().toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Shipping</span>
                <span className="font-semibold text-emerald-600">Free</span>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between text-base font-black text-[#1A1A1A]">
                  <span>Total</span>
                  <span>৳{getCartTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Link to="/checkout" className="sc-btn-primary mt-6 block w-full rounded-xl py-3 text-center">
              Proceed to checkout
            </Link>

            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Coupon</p>
              <div className="mt-2 flex gap-2">
                <input type="text" className="sc-input text-sm" placeholder="Enter code" />
                <button className="rounded-lg bg-[#1A1A1A] px-3 text-sm font-semibold text-white transition hover:bg-[#DB4444]">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
